// Basic
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useSessionStore from '@/app/store/session';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useCustomPayStore from '@/app/store/customPay';
import actionsSims from '../../api/actionsSims';
// Components
import ModalUpdateSimPlan from '../modals/ModalUpdateSimPlan';
import ModalUpdateSimCard from '../modals/ModalUpdateSimCard';
import ModalActivateSimCard from '../modals/activateSimCard/ModalActivateSimCard';
import ModalAskConfirmation from '@/common-utils/components/renders/modal/components/modals/ModalAskConfirmation';
import DetailsGrid from '@/common-utils/components/renders/detailsGrid/DetailsGrid';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import RenderSimConsumptionChart from '../dataUnit/RenderSimConsumptionChart';
import Entity from '@/common-utils/components/renders/entity/Entity';
import CustomPayModalActivateSimCard from '../modals/activateSimCard/CustomPayModalActivateSimCard';
// Common
import simCardFields from '../../fields/simCardFields';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import generateSimCardActionsLifecycle from '../dropdown/actions/generateSimCardActionsLifecycle';
import SIM_CARDS_STATUS from '../../enums/SimCardStatus';
import { flattenFieldsObjects } from '@/common-utils/functions/flattenFieldsObjects';
import { toast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
import getIccidWithoutPrefix from '@/common-utils/functions/getIccidWithoutPrefix';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
import { useTranslation } from 'react-i18next';

/**
 * @param root0
 * @param root0.simCard
 * @param root0.refetch
 * @returns {JSX} SimDetails
 */
function SimDetails({ simCard, refetch }) {

    // Lang
    const { i18n } = useTranslation();
    const lang = i18n.resolvedLanguage;

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const { permissions: userPermissions } = useSessionStore(s => s.session) ?? {};
    const isCustomPay = useCustomPayStore((s) => s.isCustomPay);

    // Constants
    const { uuid: simCardId } = simCard?.sim ?? {};

    // State
    const [showModalActivateSimCard, setShowModalActivateSimCard] = useState(false);
    const handleShowModalActivateSimCard = () => {
        setShowModalActivateSimCard(true);
    };
    const [showCustomPayModalActivateSimCard, setShowCustomPayModalActivateSimCard] = useState(false);
    const handleShowCustomPayModalActivateSimCard = () => {
        setShowCustomPayModalActivateSimCard(true);
    };
    const [showModalAskConfirmationReactivateSimCard, setShowModalAskConfirmationReactivateSimCard] = useState(false);
    const handleShowModalAskConfirmationReactivateSimCard = () => {
        setShowModalAskConfirmationReactivateSimCard(true);
    };
    const [showModalUpdateSimCard, setShowModalUpdateSimCard] = useState(false);
    const handleShowModalUpdateSimCard = () => {
        setShowModalUpdateSimCard(true);
    };
    const [showModalUpdateSimPlan, setShowModalUpdateSimPlan] = useState(false);
    const handleShowModalUpdateSimPlan = () => {
        setShowModalUpdateSimPlan(true);
    };
    const [showModalAskConfirmationTerminateSimCard, setShowModalAskConfirmationTerminateSimCard] = useState(false);
    const handleShowModalAskConfirmationTerminateSimCard = () => {
        setShowModalAskConfirmationTerminateSimCard(true);
    };
    const [showModalAskConfirmationSuspendSimCard, setShowModalAskConfirmationSuspendSimCard] = useState(false);
    const handleShowModalAskConfirmationSuspendSimCard = () => {
        setShowModalAskConfirmationSuspendSimCard(true);
    };

    // API Calls reactivate SimCard
    const onSuccessReactivateSimCard = () => {
        toast({
            description: <Entity entity={'modal.reactivate.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
        refetch(true);
    };
    const onErrorReactivateSimCard = () => {
        setShowModalAskConfirmationReactivateSimCard(false);
        toast({
            description: <Entity entity={'modal.reactivate.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitReactivateSimCard = actionsSims.reactivateSimCard(onSuccessReactivateSimCard, onErrorReactivateSimCard);

    // API Calls suspend SimCard
    const onSuccessSuspendSimCard = () => {
        toast({
            description: <Entity entity={'modal.suspend.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
        refetch();
    };
    const onErrorSuspendSimCard = () => {
        setShowModalAskConfirmationSuspendSimCard(false);
        toast({
            description: <Entity entity={'modal.activate.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitSuspendSimCard = actionsSims.suspendSimCard(onSuccessSuspendSimCard, onErrorSuspendSimCard);

    // API Calls terminate SimCard
    const onSuccessTerminateSimCard = () => {
        toast({
            description: <Entity entity={'modal.terminate.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
        refetch();
    };
    const onErrorTerminateSimCard = () => {
        setShowModalAskConfirmationTerminateSimCard(false);
        toast({
            description: <Entity entity={'modal.terminate.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitTerminateSimCard = actionsSims.terminateSimCard(onSuccessTerminateSimCard, onErrorTerminateSimCard);

    // Hooks
    const fullOfferName = useMemo(() => {
        const data = getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.DATA));
        const dataUnit = getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.DATA_UNIT));

        return data && dataUnit ? `${data} ${getTranslatedText(`units.${dataUnit}`, nsSimCard)}` : null;
    }, [lang]);

    // Functions
    // Concatenate offer data with offer data unit to get full offer
    const appendFullOffer = () => {
        return {
            [simCardFields.SIM_CARD.key]: {
                ...simCard[simCardFields.SIM_CARD.key],
                [simCardFields.SIM_CARD.DATA.key]: fullOfferName
            }
        };
    };

    // Constants
    const simCardCopy = { ...simCard, ...appendFullOffer() };
    simCardCopy.sim.iccid = getIccidWithoutPrefix(simCardCopy.sim.iccid);
    const detailsKeys = [
        'SIM_CARD.STATUS',
        'PROVIDER.LEGAL_NAME',
        'SIM_CARD.LABEL',
        'SIM_CARD.ICCID',
        // 'SIM_CARD.MNO_NAME', // TODO: Uncomment this when the backend is updated
        'SIM_CARD.DATA',
        'SIM_CARD.APNS',
        'SIM_CARD.FIRST_COMM_DATE',
        'SIM_CARD.LAST_COMM_DATE',
        'SIM_CARD.END_OF_ENGAGEMENT_DATE',
        'SIM_CARD.ACTIVATION_DATE',
        'SIM_CARD.ACTIVATION_REQUEST_DATE',
        'SIM_CARD.CANCELLATION_DATE',
        'SIM_CARD.CANCELLATION_REQUEST_DATE',
        'SIM_CARD.DISPATCH_DATE',
        'SIM_CARD.SUSPENSION_DATE',
        'SIM_CARD.CREATED_AT',
        'SIM_CARD.UPDATED_AT'
    ];
    const simCardDetailsFields = flattenFieldsObjects(detailsKeys, simCardFields);
    const dataUsage = getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.DATA_USAGE));

    const isIngenico = rootOrganization.uuid === '4ac85a69-8e6e-4f8f-adbb-4c62aba60dd6';
    const isSimActivated = simCard ?
        getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.STATUS)) === SIM_CARDS_STATUS.ACTIVATED.name : false;
    const isPlanUpdatable = simCard ?
        getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.NEXT_OFFER)) === null
        && getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.OFFER_CAN_BE_UPDATED)) === true : false;
    const organizationLegalName = simCard ? getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.CLIENT.LEGAL_NAME)) : null;

    return (
        <div className='p-2'>
            {
                simCard &&
                <PageActions
                    actions={[
                        ...generateSimCardActionsLifecycle(
                            simCard.sim,
                            userPermissions,
                            {
                                isCustomPay,
                                isSimActivated,
                                isPlanUpdatable,
                                shouldShowPayviewAction: isCustomPay,
                                shouldShowTerminateAction: isCustomPay && isIngenico || !isCustomPay,
                                activateSimCard: isCustomPay ? undefined : handleShowModalActivateSimCard,
                                // PATCH - VP-5267 - Desactivate action in SIM details page, reuse the line below after backend fix
                                // activateSimCard: isCustomPay ? handleShowCustomPayModalActivateSimCard : handleShowModalActivateSimCard,
                                reactivateSimCard: handleShowModalAskConfirmationReactivateSimCard,
                                terminateSimCard: handleShowModalAskConfirmationTerminateSimCard,
                                suspendSimCard: handleShowModalAskConfirmationSuspendSimCard,
                                updateLabel: handleShowModalUpdateSimCard,
                                updatePlan: handleShowModalUpdateSimPlan
                            }
                        )
                    ]}
                    retractable
                />
            }
            <DetailsGrid
                data={simCardCopy}
                fields={simCardDetailsFields}
                permissions={userPermissions}
                showEmptyFields={false}
            />
            {!!dataUsage.length &&
                <RenderSimConsumptionChart
                    data={dataUsage}
                    simOffer={
                        {
                            tariffData: getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.DATA)),
                            tariffDataUnit: getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.DATA_UNIT))
                        }
                    }
                />
            }
            {
                showModalActivateSimCard &&
                <ModalActivateSimCard
                    isOpen={showModalActivateSimCard}
                    setIsOpen={setShowModalActivateSimCard}
                    simCardId={simCardId}
                    setLaunchRefetch={refetch}
                />
            }
            {
                showCustomPayModalActivateSimCard &&
                <CustomPayModalActivateSimCard
                    isOpen={showCustomPayModalActivateSimCard}
                    setIsOpen={setShowCustomPayModalActivateSimCard}
                    simCardId={simCardId}
                    setLaunchRefetch={refetch}
                />
            }
            {
                showModalAskConfirmationReactivateSimCard &&
                <ModalAskConfirmation
                    entityTitle='modal.reactivate.title'
                    entityBody='modal.reactivate.text'
                    confirmChoice={() => {
                        submitReactivateSimCard.mutate({
                            uuids: Array.isArray(simCardId) ? simCardId : [simCardId]
                        });
                    }}
                    namespace={nsSimCard}
                    isLoading={submitReactivateSimCard.isLoading}
                    isOpen={showModalAskConfirmationReactivateSimCard} setIsOpen={setShowModalAskConfirmationReactivateSimCard}
                />
            }
            {
                showModalUpdateSimCard &&
                <ModalUpdateSimCard
                    isOpen={showModalUpdateSimCard}
                    setIsOpen={setShowModalUpdateSimCard}
                    sim={simCard.sim}
                    setLaunchRefetch={refetch}
                />
            }
            {
                showModalUpdateSimPlan &&
                <ModalUpdateSimPlan
                    isOpen={showModalUpdateSimPlan}
                    setIsOpen={setShowModalUpdateSimPlan}
                    simCardId={simCardId}
                    organizationName={organizationLegalName}
                    setLaunchRefetch={refetch}
                />
            }
            {
                showModalAskConfirmationTerminateSimCard &&
                <ModalAskConfirmation
                    entityTitle='modal.terminate.title'
                    entityBody='modal.terminate.text'
                    confirmChoice={() => {
                        submitTerminateSimCard.mutate({
                            uuids: Array.isArray(simCardId) ? simCardId : [simCardId]
                        });
                    }}
                    namespace={nsSimCard}
                    isLoading={submitTerminateSimCard.isLoading}
                    isOpen={showModalAskConfirmationTerminateSimCard} setIsOpen={setShowModalAskConfirmationTerminateSimCard}
                />
            }
            {
                showModalAskConfirmationSuspendSimCard &&
                <ModalAskConfirmation
                    entityTitle='modal.suspend.title'
                    entityBody='modal.suspend.text'
                    confirmChoice={() => {
                        submitSuspendSimCard.mutate({
                            uuids: Array.isArray(simCardId) ? simCardId : [simCardId]
                        });
                    }}
                    namespace={nsSimCard}
                    isLoading={submitSuspendSimCard.isLoading}
                    isOpen={showModalAskConfirmationSuspendSimCard} setIsOpen={setShowModalAskConfirmationSuspendSimCard}
                />
            }
        </div>
    );
}

SimDetails.propTypes = {
    simCard: PropTypes.object,
    refetch: PropTypes.func.isRequired
};

export default SimDetails;